<template>
  <section
    class="container mx-auto max-w-screen-xl p-4 md:p-12 bg-cover bg-no-repeat relative"
    :style="bgImage ? { backgroundImage: `url(${bgImage})` } : {}"
  >
    <div class="inset-0 bg-gradient-to-r from-primary-500/80 to-primary-500/80 absolute"></div>

    <div class="relative rounded-2xl overflow-hidden bg-white">
      <Swiper
        :modules="[Navigation, Pagination, Autoplay]"
        :autoplay="autoplay ? { delay: autoplayDelay } : false"
        :loop="false"
        :pagination="{ el: '.big-slider-progress', type: 'progressbar' }"
        :navigation="{ prevEl: '.big-slider-prev', nextEl: '.big-slider-next' }"
        class="h-[600px] md:h-[650px]"
      >
        <SwiperSlide v-for="(item, index) in sliderItems" :key="index">
          <div class="flex flex-col md:flex-row h-full">
            <div class="flex flex-col items-center p-4 md:p-8 relative h-3/4 md:w-1/2 md:h-full order-1 md:order-0">
              <h2 v-if="item.title" class="text-3xl font-bold mb-4">
                {{ item.title }}
              </h2>
              <p v-if="item.subtitle" class="text-gray-600 mb-6">
                {{ item.subtitle }}
              </p>

              <a
                :href="item.link"
                v-if="item.productImage"
                class="h-full mx-auto mb-16 block relative z-[21] pointer-events-auto"
              >
                <img
                  v-if="item.productImage"
                  :src="item.productImage"
                  alt=""
                  class="h-full max-h-[250px] md:max-h-[400px] w-auto object-contain"
                />
              </a>
            </div>

            <div class="relative h-1/4 md:w-1/2 md:h-full order-0 md:order-1">
              <video
                :ref="(el) => (videoRefs[index] = el as HTMLVideoElement)"
                v-if="item.productVideo"
                :src="item.productVideo"
                autoplay
                muted
                loop
                playsinline
                class="w-full h-full object-cover"
              />
              <img
                v-else-if="item.productHeroImage"
                :src="item.productHeroImage"
                alt=""
                class="w-full h-full object-cover"
              />

              <button
                v-if="item.productVideo"
                @click="toggleVideo(index)"
                class="absolute bottom-4 right-4 z-20 bg-white w-14 h-14 flex items-center justify-center p-2"
              >
                <VcIcon v-if="isPlaying" name="pause-custom" class="flex items-center justify-center" :size="20" />
                <VcIcon v-else name="play-custom" class="flex items-center justify-center" :size="20" />
              </button>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>

      <div class="absolute w-full md:w-1/2 bottom-[50px] left-0">
        <div class="big-slider-progress !w-56 h-2 rounded overflow-hidden relative bg-gray-200"></div>
      </div>

      <div
        v-if="sliderItems.length > 1"
        class="absolute left-0 w-full md:w-1/2 flex items-center justify-between px-4 md:px-8 space-x-4 z-20 bottom-[200px] md:bottom-unset md:top-[50%] transform-[-50%] h-[50px] pointer-events-none"
      >
        <button
          aria-label="prev"
          class="big-slider-prev w-10 h-10 md:w-16 md:h-16 flex items-center justify-center border shadow-sm bg-white pointer-events-auto"
        >
          <VcIcon class="relative flex justify-center items-center" name="chevron-left-small" :size="20" />
        </button>
        <button
          aria-label="next"
          class="big-slider-next w-10 h-10 md:w-16 md:h-16 flex items-center justify-center border shadow-sm bg-white pointer-events-auto"
        >
          <VcIcon class="flex justify-center items-center relative left-1" name="chevron-right-small" :size="20" />
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export interface SliderItem {
  title?: string;
  subtitle?: string;
  link?: string;
  productImage?: string;
  productVideo?: string;
  productHeroImage?: string;
}

interface IProps {
  bgImage?: string;
  sliderItems: SliderItem[];
}

const props = defineProps<IProps>();
const { bgImage, sliderItems } = props;
const autoplay = false;
const autoplayDelay = 5000;
const isPlaying = ref(true);
const videoRefs = ref<Array<HTMLVideoElement | null>>([]);

function toggleVideo(index: number) {
  const v = videoRefs.value[index];
  if (!v) return;
  if (v.paused) {
    isPlaying.value = true;
    v.play().catch(() => {});
  } else {
    isPlaying.value = false;
    v.pause();
  }
}
</script>

<style lang="scss">
.big-slider-progress {
  left: 50% !important;
  transform: translateX(-50%);

  & .swiper-pagination-progressbar {
    background: rgba(0, 0, 0, 0.08);
    height: 6px;
    border-radius: 9999px;
    overflow: hidden;
  }

  & .swiper-pagination-progressbar-fill {
    @apply bg-primary-800 h-full;
  }
}
</style>
